import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import url from '../../../helpers/url';
import { useTvod } from '../../../hooks/useTvod';
import { getUserInfo } from '../../../utils/user';
import { tvodGenerateOTPApi } from '../../../services/serviceWrapper/tvodServiceWrapper';
import h from '../../../helpers/common';
import { __ } from '../../../translations';
import { toast } from '@intigral/shared';
import playerService from '../../../helpers/bitmovinPlayer';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { getItemProperties } from '../../../contexts/MixpanelHelper';
import user from '../../../helpers/user';
import { useSelector } from 'react-redux';
import { IStore } from '../../../interfaces/StoreTypes';
import { useInterval } from '@intigral/hooks';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { ISubscriptionService } from '@intigral/domain/lib/application/services/SubscriptionService';
import { useUserWatchList } from '../../../hooks/watchlist/useUserWatchlist';
import { useToggleWatchlistItem } from '../../../hooks/watchlist/useToggleWatchlist';
import { isGuestUser, showGuestPromotionPopup } from '../../../config/guestMode';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';

export const useDetailOverlayActions = (item, inView, scrollRef, promotionProviderImages) => {
    // state
    const [isLoading, setIsLoading] = useState(false);
    const [showTvodPurchaseOverlay, setShowTvodPurchaseOverlay] = useState(false);
    const [openQRModal, setOpenQRModal] = useState(false);
    const [showAutoTrailer, setShowAutoTrailer] = useState(false);
    const [showTrailer, setShowTrailer] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [trailerUrl, setTrailerUrl] = useState(null);
    const [runtimeLength, setRuntimeLength] = useState(0);
    const [showMoviePoster, setShowMoviePoster] = useState(true);
    const [optimisticAdded, setOptimisticAdded] = useState<boolean | null>(null);
    const trailerHistoryPushedRef = useRef(false);
    const trailerStartTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inViewRef = useRef(inView);
    const showTrailerRef = useRef(showTrailer);

    inViewRef.current = inView;
    showTrailerRef.current = showTrailer;

    /** Delay before auto-playing the poster trailer so a quick scroll can cancel it cleanly. */
    const TRAILER_START_DELAY_MS = 500;

    const clearTrailerStartTimer = () => {
        if (trailerStartTimerRef.current) {
            clearTimeout(trailerStartTimerRef.current);
            trailerStartTimerRef.current = null;
        }
    };

    const stopAutoTrailerPlayback = () => {
        clearTrailerStartTimer();
        setTrailerUrl(null);
    };

    const scheduleAutoTrailerPlayback = () => {
        clearTrailerStartTimer();
        trailerStartTimerRef.current = setTimeout(() => {
            trailerStartTimerRef.current = null;
            // User may have scrolled to More Like This or opened the full trailer during the delay.
            if (inViewRef.current || showTrailerRef.current) return;
            setTrailerUrl(item?.trailerMediaUrl ?? null);
        }, TRAILER_START_DELAY_MS);
    };

    // redux state
    const geolocation = useSelector((store: IStore) => store.location);
    const selectedProfile = get(STORE_NAME.SELECTED_PROFILE);

    // hooks
    const { priceInfo, isTvod, isRented, productId, setTvodList, expiryDate } = useTvod(item);
    const { track }: { track: any } = useContext(MixpanelContext);
    const [searchParams] = useSearchParams();
    const currentUser = getUserInfo();
    const isGuest = isGuestUser(currentUser);
    const selectedProfileGuid = selectedProfile?.profileGuid;
    const { data } = useUserWatchList(
        selectedProfileGuid,
        currentUser,
        geolocation.countryCode,
        promotionProviderImages,
        'en',
        '',
        !isGuest
    );

    const toggleWatchlistMutation = useToggleWatchlistItem();
    const qitafRedirectPrams = url.getQuery('qitafRedirect') || searchParams.get('qitafRedirect');
    const locationSearch = new URLSearchParams(window.location.search);
    const searchString = locationSearch?.toString() || searchParams?.toString();
    const isAdded = optimisticAdded ?? data?.some((e: any) => e.Id === item?.Id);

    // handler functions
    const handleGenerateOtpApi = async () => {
        if (showGuestPromotionPopup()) {
            return;
        }

        if (currentUser?.segment === 'B2B' || currentUser?.segment === 'B2B2C') {
            setShowTvodPurchaseOverlay(!isRented);
            return;
        }
        setIsLoading(true);
        try {
            await tvodGenerateOTPApi({ productId });
            setShowTvodPurchaseOverlay(!isRented);
        } catch (error) {
            if (error?.data?.code === 'N_|_TVOD_V_OP_INV' || error?.data?.code === '9901') {
                if (h.qrCodePurchaseEnable()) {
                    setOpenQRModal(true);
                }
            } else {
                if (error?.data?.error?.code) {
                    toast(__(`err_msg_${error?.data?.error?.code}`), 'error');
                } else {
                    toast(__(`err_msg_${error?.data?.code}`), 'error');
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleMute = () => {
        playerService.toggleMute();
        setIsMuted(!isMuted);
    };

    const handleShowTrailer = async () => {
        track(`${h.getScreen()} - ${EVENT?.VOD_DETAILS?.WATCH_TRAILER}`, getItemProperties(item));
        stopAutoTrailerPlayback();
        setShowTrailer(true);
    };

    const closeTrailerPlayer = () => {
        if (trailerHistoryPushedRef.current) {
            trailerHistoryPushedRef.current = false;
            window.history.back();
            return;
        }
        setShowTrailer(false);
    };

    const handleToggleWatchlistItem = (e) => {
        if (isGuest) {
            return;
        }
        const nextValue = !isAdded;
        setOptimisticAdded(nextValue);
        toggleWatchlistMutation.mutate({
            itemId: item?.Id,
            category: item.category?.toLowerCase() ?? '',
            isAdded,
        });
    };

    const getSubscriptions = async () => {
        const currentUser = user.getUserInfo();
        const subscriptionService = container.resolve<ISubscriptionService>(DI.SERVICES.ISubscriptionService);
        const subscription = await subscriptionService.getSubscriptionStatus(
            currentUser.apiKey,
            currentUser.username,
            currentUser.authToken,
            currentUser.identityToken
        );
        return subscription?.tvodList || [];
    };

    const { start, stop } = useInterval(async () => {
        const tvodList = await getSubscriptions();
        const rentedItem = tvodList.find((item: any) => item.productId === productId);
        if (rentedItem?.expiryDate) {
            setTvodList(tvodList);
            const user = getUserInfo();
            const updatedUser = { ...user, tvodList };
            set(STORE_NAME.USER, updatedUser);
            stop();
            setIsLoading(false);
        }
    }, 2000);

    const handlePurchase = () => {
        setIsLoading(true);
        start();
    };

    //useEffects
    useEffect(() => {
        if (showTrailer) {
            window.history.pushState({ detailOverlayTrailer: true }, '');
            trailerHistoryPushedRef.current = true;
        }
    }, [showTrailer]);

    useEffect(() => {
        const handlePopState = () => {
            trailerHistoryPushedRef.current = false;
            setShowTrailer(false);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    useEffect(() => {
        scrollRef?.current?.focus();
    }, [searchString]);

    useEffect(() => {
        if (qitafRedirectPrams && productId) {
            handleGenerateOtpApi();
        }
    }, [productId]);

    useEffect(() => {
        setShowMoviePoster(true);
        const posterTimer = setTimeout(() => {
            setShowMoviePoster(false);
        }, 4000);
        return () => clearTimeout(posterTimer);
    }, [searchString]);

    useEffect(() => {
        if (!showMoviePoster) {
            setShowAutoTrailer(true);
        }
    }, [showMoviePoster]);

    // Drive poster trailer URL from one place so inView can cancel a pending delayed start.
    // Setting trailerUrl late after a quick scroll previously raced More Like This hover trailers.
    useEffect(() => {
        if (showTrailer || inView) {
            stopAutoTrailerPlayback();
            return;
        }

        if (showMoviePoster) {
            stopAutoTrailerPlayback();
            return;
        }

        if (showAutoTrailer && item?.trailerMediaUrl) {
            scheduleAutoTrailerPlayback();
        } else {
            stopAutoTrailerPlayback();
        }

        return () => clearTrailerStartTimer();
    }, [showTrailer, inView, showMoviePoster, showAutoTrailer, item?.trailerMediaUrl]);

    useEffect(() => () => clearTrailerStartTimer(), []);

    return {
        isLoading,
        showTvodPurchaseOverlay,
        openQRModal,
        showAutoTrailer,
        showTrailer,
        isMuted,
        trailerUrl,
        runtimeLength,
        showMoviePoster,
        priceInfo,
        isTvod,
        isRented,
        productId,
        expiryDate,
        toggleWatchlistMutation,
        isAdded,
        setTvodList,
        setShowTvodPurchaseOverlay,
        setOpenQRModal,
        handleShowTrailer,
        closeTrailerPlayer,
        handleGenerateOtpApi,
        handleToggleWatchlistItem,
        setShowTrailer,
        handleMute,
        setRuntimeLength,
        handlePurchase,
    };
};
